import { cn } from '@/lib/utils'
import { CalendarIcon, X } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { Button } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'

interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  id?: string
  'aria-label'?: string
  fromYear?: number
  toYear?: number
  disableFutureDates?: boolean
  clearable?: boolean
  disableDate?: (date: Date) => boolean
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  disabled = false,
  className,
  id,
  'aria-label': ariaLabel,
  fromYear = 1940,
  toYear = Math.max(new Date().getFullYear(), 2030),
  disableFutureDates = false,
  clearable = true,
  disableDate,
}: Readonly<DatePickerProps>) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState<number>(
    value?.getMonth() ?? new Date().getMonth()
  )
  const [selectedYear, setSelectedYear] = useState<number>(
    value?.getFullYear() ?? new Date().getFullYear()
  )
  const [calendarDate, setCalendarDate] = useState<Date>(
    value ? new Date(value.getFullYear(), value.getMonth(), 1) : new Date()
  )
  const [monthSelectOpen, setMonthSelectOpen] = useState(false)
  const [yearSelectOpen, setYearSelectOpen] = useState(false)

  // Update internal state when value prop changes
  useEffect(() => {
    if (value) {
      const vMonth = value.getMonth()
      const vYear = value.getFullYear()
      setSelectedMonth(vMonth)
      setSelectedYear(vYear)
      setCalendarDate(new Date(vYear, vMonth, 1))
    }
  }, [value])

  useEffect(() => {
    if (popoverOpen) {
      setCalendarDate(new Date(selectedYear, selectedMonth, 1))
    }
  }, [popoverOpen, selectedMonth, selectedYear])

  const handleCalendarMonthChange = (date: Date) => {
    setCalendarDate(date)
    setSelectedMonth(date.getMonth())
    setSelectedYear(date.getFullYear())
  }

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    onChange?.(undefined)
    setPopoverOpen(false)
  }

  const handleDateSelect = (date: Date | undefined) => {
    onChange?.(date)
    setPopoverOpen(false)
  }

  const handleMonthChange = (monthValue: string) => {
    const month = Number.parseInt(monthValue, 10)
    setSelectedMonth(month)
    setCalendarDate(new Date(selectedYear, month, 1))
  }

  const handleYearChange = (yearValue: string) => {
    const year = Number.parseInt(yearValue, 10)
    setSelectedYear(year)
    setCalendarDate(new Date(year, selectedMonth, 1))
  }

  return (
    <div className={cn('relative')}>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <div className="relative w-full">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              id={id}
              className={cn(
                'w-full min-w-0 h-11 justify-start text-left font-normal border-ss-neutral-200 bg-ss-neutral-white hover:border-ss-blue-700 focus:border-ss-blue-700 pr-8 disabled:bg-ss-neutral-100 disabled:text-ss-neutral-25 disabled:hover:cursor-not-allowed disabled:opacity-50',
                className
              )}
              aria-label={ariaLabel}
              type="button"
              disabled={disabled}
            >
              <span className="flex-1 truncate text-ellipsis overflow-hidden">
                {value ? (
                  <span className="text-ss-text-primary">
                    {value.toLocaleDateString()}
                  </span>
                ) : (
                  <span className="text-ss-neutral-400">{placeholder}</span>
                )}
              </span>
              {!value && (
                <CalendarIcon
                  className="h-4 w-4 text-ss-neutral-600"
                  aria-hidden="true"
                />
              )}
            </Button>
          </PopoverTrigger>
          {value && clearable && (
            <X
              className="absolute top-1/2 -translate-y-1/2 right-2 h-4 w-4 text-ss-neutral-400 hover:text-ss-neutral-600 cursor-pointer z-20"
              onClick={handleClear}
              aria-label="Clear date"
            />
          )}
          {!clearable && value && (
            <CalendarIcon
              className="absolute top-1/2 -translate-y-1/2 right-2 h-4 w-4 text-ss-neutral-400 hover:text-ss-neutral-600 cursor-pointer z-20"
              aria-hidden="true"
            />
          )}
        </div>
        <PopoverContent className="w-auto p-4" align="start">
          <div className="space-y-4">
            {/* Month and Year Dropdowns */}
            <div className="flex gap-2">
              <Select
                value={selectedMonth.toString()}
                onValueChange={handleMonthChange}
                open={monthSelectOpen}
                onOpenChange={setMonthSelectOpen}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="h-48 overflow-auto">
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem
                      key={i}
                      value={i.toString()}
                      data-value={i.toString()}
                    >
                      {new Date(2000, i, 1).toLocaleDateString('en-US', {
                        month: 'long',
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={selectedYear.toString()}
                onValueChange={handleYearChange}
                open={yearSelectOpen}
                onOpenChange={setYearSelectOpen}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="h-48 overflow-auto">
                  {Array.from({ length: toYear - fromYear + 1 }, (_, i) => {
                    const year = toYear - i
                    return (
                      <SelectItem
                        key={year}
                        value={year.toString()}
                        data-value={year.toString()}
                      >
                        {year}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Calendar */}
            <Calendar
              className="w-full"
              mode="single"
              selected={value}
              onSelect={handleDateSelect}
              aria-label="Calendar for date selection"
              disabled={
                typeof disableDate === 'function'
                  ? disableDate
                  : disableFutureDates
                    ? { after: new Date() }
                    : undefined
              }
              month={calendarDate}
              onMonthChange={handleCalendarMonthChange}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
